// frontend/src/pages/quotes/QuoteView.js
import { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
    ArrowLeft, Download, CheckCircle, XCircle, Clock, Edit3, FileText,
    Ship, Globe, Calendar, Percent, Users, Building, MapPin, Anchor,
    AlertCircle, Shield, UserCheck, FileCheck, Printer, ChevronDown,
    ChevronUp, Loader2, Copy, Check, X, TrendingUp, Landmark,
    Paperclip, Eye, Trash2,
} from 'lucide-react';
import api from '../../services/api';
import Badge from '../../components/ui/Badge';

// ---------------------------------------------------------------------------
// Date/time helpers
//
// The backend uses `dateStrings: true` on the MySQL pool, so DATETIME values
// arrive as naive strings such as "2026-10-08 12:49:12". Those values are
// already Nairobi wall-clock time (the DB server writes them from NOW()).
//
// We intentionally do NOT use `new Date(...)` or `.toLocaleString(...)` here:
//   - `new Date("YYYY-MM-DD HH:mm:ss")` is not spec-guaranteed and varies
//     between browsers and Node.
//   - Passing `timeZone: 'Africa/Nairobi'` to toLocaleString will re-shift a
//     value that is already Nairobi time, producing a 3-hour error on any
//     machine not already in EAT.
//
// Instead we parse the string and print its components verbatim.
// ---------------------------------------------------------------------------
const MONTH_ABBR = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

function parseNaiveDateTime(val) {
    if (!val) return null;
    const s = String(val).trim();
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/);
    if (!m) return null;
    return {
        year: m[1],
        month: m[2],
        day: m[3],
        hour: m[4],
        minute: m[5],
        second: m[6] || '00',
    };
}

function formatDate(val) {
    const p = parseNaiveDateTime(val);
    if (!p) return val || 'N/A';
    const monthName = MONTH_ABBR[Number(p.month) - 1] || p.month;
    return `${p.day} ${monthName} ${p.year}`;
}

function formatDateTime(val) {
    const p = parseNaiveDateTime(val);
    if (!p) return val || 'N/A';
    return `${p.day}/${p.month}/${p.year}, ${p.hour}:${p.minute}:${p.second}`;
}

// ---------------------------------------------------------------------------
// Other helpers
// ---------------------------------------------------------------------------

function toNumber(value, fallback = 0) {
    if (value === null || value === undefined || value === '') return fallback;
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
}

function formatMoney(val, currency, withDecimals = false) {
    const n = toNumber(val, 0);
    const prefix = currency === 'KES' ? 'KSh ' : '$';
    return `${prefix}${n.toLocaleString(undefined, withDecimals
        ? { minimumFractionDigits: 2, maximumFractionDigits: 2 }
        : {}
    )}`;
}

function getBasename(filePath) {
    if (!filePath) return '';
    const cleaned = String(filePath).split('?')[0];
    const parts = cleaned.split(/[\\/]/);
    return parts[parts.length - 1];
}

const REJECT_REASONS = [
    'Insufficient documentation provided',
    'Sum insured exceeds approved limits',
    'Rates do not match underwriter guidelines',
    'Route is on high-risk list',
    'Client credit check required',
];

const STATUS_META = {
    approved: {
        label: 'Quote Approved',
        icon: CheckCircle,
        gradient: 'from-[#1e3b29] via-[#3a663f] to-[#4d8c54]',
        iconColor: 'text-green-100',
    },
    rejected: {
        label: 'Quote Rejected',
        icon: XCircle,
        gradient: 'from-red-600 to-red-700',
        iconColor: 'text-red-100',
    },
    submitted: {
        label: 'Pending Review',
        icon: Clock,
        gradient: 'from-amber-500 to-amber-600',
        iconColor: 'text-amber-100',
    },
    draft: {
        label: 'Draft',
        icon: FileText,
        gradient: 'from-slate-500 to-slate-600',
        iconColor: 'text-slate-100',
    },
};

const ATTACHMENT_FIELDS = [
    { key: 'kra_pin_attachment', label: 'KRA PIN Certificate' },
    { key: 'certificate_incorporation_attachment', label: 'Certificate of Incorporation' },
    { key: 'commercial_invoice_attachment', label: 'Commercial Invoice' },
    { key: 'waybill_attachment', label: 'Waybill' },
    { key: 'bill_of_lading_attachment', label: 'Bill of Lading' },
];

const API_BASE = process.env.REACT_APP_API_URL || '';

export default function QuoteView() {
    const { id } = useParams();
    const { user } = useSelector((s) => s.auth);
    const navigate = useNavigate();

    const [quote, setQuote] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [showRejectModal, setShowRejectModal] = useState(false);
    const [showApproveConfirm, setShowApproveConfirm] = useState(false);
    const [showConvertConfirm, setShowConvertConfirm] = useState(false);
    const [rejectionReason, setRejectionReason] = useState('');
    const [approving, setApproving] = useState(false);
    const [rejecting, setRejecting] = useState(false);
    const [convertingToCargo, setConvertingToCargo] = useState(false);
    const [pdfDownloading, setPdfDownloading] = useState(false);
    const [removingAttachment, setRemovingAttachment] = useState(null);
    const [removingAdditional, setRemovingAdditional] = useState(null);

    const [amendMode, setAmendMode] = useState(false);
    const [amendForm, setAmendForm] = useState({});
    const [amendErrors, setAmendErrors] = useState({});
    const [savingAmend, setSavingAmend] = useState(false);

    const [expandedSections, setExpandedSections] = useState({
        parties: true,
        shipment: true,
        interests: true,
        premium: true,
        attachments: true,
        additionalAttachments: true,
    });
    const [copiedRef, setCopiedRef] = useState(false);
    const [toast, setToast] = useState(null);

    const printRef = useRef();

    const canApprove = user?.role === 'underwriter' || user?.role === 'admin';

    const pushToast = useCallback((type, message) => {
        const id = Date.now();
        setToast({ id, type, message });
        setTimeout(() => setToast((t) => (t?.id === id ? null : t)), 4000);
    }, []);

    const fetchQuote = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const { data } = await api.get(`/quotes/${id}`);
            setQuote(data);
            setAmendForm({
                marine_rate: data.marine_rate != null
                    ? String(data.marine_rate)
                    : (data.marine_premium && data.sum_insured
                        ? ((data.marine_premium / data.sum_insured) * 100).toFixed(2)
                        : '0.20'),
                war_rate: data.war_rate != null
                    ? String(data.war_rate)
                    : (data.war_premium && data.sum_insured
                        ? ((data.war_premium / data.sum_insured) * 100).toFixed(2)
                        : '0.05'),
                sum_insured: data.sum_insured || '',
                custom_bank_interest: data.custom_bank_interest || '',
                amendment_notes: '',
                interests_insured: data.interests_insured || '',
                comments_conditions: data.comments_conditions || '',
                from_location: data.from_location || '',
                to_location: data.to_location || '',
                insured_name: data.insured_name || '',
            });
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to load quote');
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchQuote();
    }, [fetchQuote]);

    const handleApprove = async () => {
        setApproving(true);
        try {
            await api.put(`/quotes/${id}/approve`);
            setShowApproveConfirm(false);
            pushToast('success', 'Quote approved successfully');
            fetchQuote();
        } catch (err) {
            pushToast('error', err.response?.data?.error || 'Approval failed');
        } finally {
            setApproving(false);
        }
    };

    const handleReject = async () => {
        if (!rejectionReason.trim()) return;
        setRejecting(true);
        try {
            await api.put(`/quotes/${id}/reject`, { reason: rejectionReason });
            setShowRejectModal(false);
            setRejectionReason('');
            pushToast('success', 'Quote rejected');
            fetchQuote();
        } catch (err) {
            pushToast('error', err.response?.data?.error || 'Rejection failed');
        } finally {
            setRejecting(false);
        }
    };

    const validateAmend = () => {
        const errors = {};
        const mr = Number(amendForm.marine_rate);
        const wr = Number(amendForm.war_rate);
        const si = Number(amendForm.sum_insured);

        if (!mr || mr <= 0) errors.marine_rate = 'Must be greater than 0';
        if (!wr || wr < 0) errors.war_rate = 'Cannot be negative';
        if (!si || si <= 0) errors.sum_insured = 'Must be greater than 0';
        return errors;
    };

    const handleAmend = async (e) => {
        e.preventDefault();
        const errors = validateAmend();
        if (Object.keys(errors).length) {
            setAmendErrors(errors);
            return;
        }
        setAmendErrors({});
        setSavingAmend(true);
        try {
            await api.put(`/quotes/${id}`, amendForm);
            setAmendMode(false);
            pushToast('success', 'Quote amended successfully');
            fetchQuote();
        } catch (err) {
            pushToast('error', err.response?.data?.error || 'Amendment failed');
        } finally {
            setSavingAmend(false);
        }
    };

    const handleConvertToCargo = async () => {
        setConvertingToCargo(true);
        try {
            const { data } = await api.post(`/quotes/${id}/convert-to-cargo`);
            navigate('/cargo', {
                state: {
                    fromQuote: true,
                    quoteData: data.quote,
                    idfData: {
                        idf_number: data.quote?.invoice_number || data.quote?.quote_number,
                        agents_company_pin: data.quote?.clearing_agent_pin || '',
                    },
                },
            });
        } catch (err) {
            pushToast('error', err.response?.data?.error || 'Only approved quotes can be converted.');
            setShowConvertConfirm(false);
        } finally {
            setConvertingToCargo(false);
        }
    };

    const handleDownloadPDF = async () => {
        setPdfDownloading(true);
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                pushToast('error', 'Session expired. Please log in again.');
                return;
            }
            const response = await fetch(
                `${API_BASE}/quotes/${id}/pdf`,
                { method: 'GET', headers: { Authorization: `Bearer ${token}` } }
            );
            if (!response.ok) throw new Error('Download failed');

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `Mayfair-Quote-${quote?.quote_number || id}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            pushToast('success', 'PDF downloaded');
        } catch (err) {
            console.error('[QuoteView] PDF download error:', err);
            pushToast('error', 'PDF download failed. Please try again.');
        } finally {
            setPdfDownloading(false);
        }
    };

    const handleCopyRef = () => {
        if (!quote?.quote_number) return;
        navigator.clipboard.writeText(quote.quote_number).then(() => {
            setCopiedRef(true);
            setTimeout(() => setCopiedRef(false), 1500);
        }).catch(() => { /* ignore */ });
    };

    const toggleSection = (section) =>
        setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));

    const handleViewAttachment = async (filePath) => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                pushToast('error', 'Session expired. Please log in again.');
                return;
            }
            const filename = getBasename(filePath);
            const response = await fetch(
                `${API_BASE}/quotes/attachments/${encodeURIComponent(filename)}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (!response.ok) throw new Error('Failed to load file');

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            window.open(url, '_blank', 'noopener,noreferrer');
            setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
        } catch (err) {
            console.error('[QuoteView] View attachment error:', err);
            pushToast('error', 'Could not open file');
        }
    };

    const handleDownloadAttachment = async (filePath, fallbackName) => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                pushToast('error', 'Session expired. Please log in again.');
                return;
            }
            const filename = getBasename(filePath) || fallbackName || 'attachment';
            const response = await fetch(
                `${API_BASE}/quotes/attachments/${encodeURIComponent(filename)}/download`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (!response.ok) throw new Error('Download failed');

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('[QuoteView] Download attachment error:', err);
            pushToast('error', 'Download failed');
        }
    };

    const handleRemoveAttachment = async (fieldKey) => {
        if (!window.confirm('Remove this attachment? This cannot be undone.')) return;
        setRemovingAttachment(fieldKey);
        try {
            await api.delete(`/quotes/${id}/named-attachments/${fieldKey}`);
            pushToast('success', 'Attachment removed');
            fetchQuote();
        } catch (err) {
            pushToast('error', err.response?.data?.error || 'Failed to remove attachment');
        } finally {
            setRemovingAttachment(null);
        }
    };

    const handleViewAdditionalAttachment = async (attachmentId) => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                pushToast('error', 'Session expired. Please log in again.');
                return;
            }
            const response = await fetch(
                `${API_BASE}/quotes/${id}/attachments/${attachmentId}/view`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (!response.ok) throw new Error('Failed to load file');

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            window.open(url, '_blank', 'noopener,noreferrer');
            setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
        } catch (err) {
            console.error('[QuoteView] View additional attachment error:', err);
            pushToast('error', 'Could not open file');
        }
    };

    const handleDownloadAdditionalAttachment = async (attachmentId, fallbackName) => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                pushToast('error', 'Session expired. Please log in again.');
                return;
            }
            const response = await fetch(
                `${API_BASE}/quotes/${id}/attachments/${attachmentId}/download`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (!response.ok) throw new Error('Download failed');

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = fallbackName || 'attachment';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('[QuoteView] Download additional attachment error:', err);
            pushToast('error', 'Download failed');
        }
    };

    const handleRemoveAdditionalAttachment = async (attachmentId) => {
        if (!window.confirm('Remove this attachment? This cannot be undone.')) return;
        setRemovingAdditional(attachmentId);
        try {
            await api.delete(`/quotes/${id}/attachments/${attachmentId}`);
            pushToast('success', 'Attachment removed');
            fetchQuote();
        } catch (err) {
            pushToast('error', err.response?.data?.error || 'Failed to remove attachment');
        } finally {
            setRemovingAdditional(null);
        }
    };

    if (loading) return <SkeletonLoader />;

    if (error || !quote) {
        return (
            <div className="min-h-screen flex items-center justify-center px-4">
                <div className="text-center max-w-md p-8">
                    <div className="w-20 h-20 mx-auto bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mb-4">
                        <AlertCircle className="w-10 h-10 text-red-500" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-2">Quote Not Found</h3>
                    <p className="text-gray-500 dark:text-slate-400">
                        {error || 'The requested quote could not be found'}
                    </p>
                    <Link
                        to="/quotes"
                        className="inline-flex items-center gap-2 mt-6 px-6 py-3 bg-brand-600 text-white rounded-lg font-medium hover:bg-brand-700 transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Back to Quotes
                    </Link>
                </div>
            </div>
        );
    }

    const meta = STATUS_META[quote.status] || STATUS_META.draft;
    const StatusIcon = meta.icon;
    const isPending = quote.status === 'submitted';
    const isApproved = quote.status === 'approved';
    const isRejected = quote.status === 'rejected';
    const isDraft = quote.status === 'draft';
    const isAutoApproved = Boolean(quote.auto_approved);

    // -------- Attachment-removal permissions --------
    // Draft    : owner + admin + underwriter
    // Submitted: admin + underwriter only
    // Approved / Rejected: nobody
    const isPrivilegedUser = user?.role === 'admin' || user?.role === 'underwriter';
    const isOwner = quote && quote.user_id === user?.id;
    const canRemoveAttachments =
        (isDraft && (isOwner || isPrivilegedUser)) ||
        (isPending && isPrivilegedUser);

    const singleAttachments = ATTACHMENT_FIELDS
        .map((f) => ({ ...f, path: quote[f.key] }))
        .filter((f) => Boolean(f.path));

    const additionalAttachments = Array.isArray(quote.additional_attachments)
        ? quote.additional_attachments
        : [];

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-slate-900 dark:to-slate-800">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">

                <div className="flex items-center justify-between print:hidden">
                    <Link
                        to="/quotes"
                        className="inline-flex items-center gap-2 text-gray-500 hover:text-brand-600 dark:text-slate-400 dark:hover:text-green-400 transition-colors font-medium focus:outline-none focus:ring-2 focus:ring-brand-500/40 rounded-lg px-2 py-1"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Back to Quotes
                    </Link>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => window.print()}
                            aria-label="Print quote"
                            className="p-2 text-gray-500 hover:text-brand-600 dark:text-slate-400 dark:hover:text-green-400 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500/40"
                        >
                            <Printer className="w-5 h-5" />
                        </button>
                        <button
                            onClick={handleDownloadPDF}
                            disabled={pdfDownloading}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white rounded-lg font-medium transition-all shadow-lg hover:shadow-xl disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-brand-500/50 focus:ring-offset-2"
                        >
                            {pdfDownloading ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Downloading...
                                </>
                            ) : (
                                <>
                                    <Download className="w-4 h-4" />
                                    Download PDF
                                </>
                            )}
                        </button>
                    </div>
                </div>

                <div className={`relative overflow-hidden rounded-2xl p-6 text-white bg-gradient-to-r ${meta.gradient} shadow-xl`}>
                    <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/3" aria-hidden />
                    <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/3" aria-hidden />

                    <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div className="flex items-start gap-4">
                            <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
                                <StatusIcon className={`w-8 h-8 ${meta.iconColor}`} />
                            </div>
                            <div>
                                <div className="flex items-center gap-3 flex-wrap">
                                    <button
                                        type="button"
                                        onClick={handleCopyRef}
                                        className="group inline-flex items-center gap-1.5 text-sm opacity-90 font-medium hover:opacity-100 transition-opacity"
                                        title="Copy reference"
                                    >
                                        <span className="font-mono">#{quote.quote_number}</span>
                                        {copiedRef ? (
                                            <Check className="w-3.5 h-3.5 text-green-300" />
                                        ) : (
                                            <Copy className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                                        )}
                                    </button>
                                    <Badge
                                        status={quote.status}
                                        className="bg-white/20 text-white border-0 text-xs px-3 py-1 rounded-full"
                                    />
                                    {isAutoApproved && (
                                        <span
                                            className="inline-flex items-center gap-1 text-xs bg-green-400/20 text-green-100 px-2 py-1 rounded-full cursor-help"
                                            title="This quote was automatically approved because it was linked to an active Open Cover"
                                        >
                                            <Shield className="w-3 h-3" />
                                            Auto-Approved (Open Cover)
                                        </span>
                                    )}
                                </div>
                                <h1 className="text-2xl md:text-3xl font-extrabold mt-1">{meta.label}</h1>
                            </div>
                        </div>

                        <div className="flex items-center gap-4 text-sm">
                            <div>
                                <p className="text-white/60 text-xs uppercase tracking-wider">Generated</p>
                                <p className="font-medium">
                                    {formatDate(quote.generated_at || quote.created_at)}
                                </p>
                            </div>
                            <div className="w-px h-10 bg-white/20" aria-hidden />
                            <div>
                                <p className="text-white/60 text-xs uppercase tracking-wider">Valid Until</p>
                                <p className="font-medium">
                                    {formatDate(quote.valid_until || Date.now() + 30 * 24 * 60 * 60 * 1000)}
                                </p>
                            </div>
                        </div>
                    </div>

                    {isRejected && quote.rejection_reason && (
                        <div className="relative mt-4 p-3 bg-red-500/20 backdrop-blur-sm rounded-lg border border-red-400/30 flex items-start gap-2">
                            <AlertCircle className="w-4 h-4 text-red-100 flex-shrink-0 mt-0.5" />
                            <div className="text-sm text-red-50">
                                <p className="font-semibold">Rejection reason</p>
                                <p className="mt-0.5">{quote.rejection_reason}</p>
                            </div>
                        </div>
                    )}
                </div>

                {canApprove && isPending && (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 print:hidden sm:static sticky bottom-4 z-20">
                        <button
                            onClick={() => setShowApproveConfirm(true)}
                            disabled={approving}
                            className="flex items-center justify-center gap-2 px-6 py-3 bg-brand-600 hover:bg-brand-700 text-white rounded-xl font-semibold transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none focus:outline-none focus:ring-2 focus:ring-brand-500/50 focus:ring-offset-2"
                        >
                            {approving ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle className="w-5 h-5" />}
                            {approving ? 'Approving...' : 'Approve Quote'}
                        </button>
                        <button
                            onClick={() => setShowRejectModal(true)}
                            disabled={rejecting}
                            className="flex items-center justify-center gap-2 px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl font-semibold transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none focus:outline-none focus:ring-2 focus:ring-red-500/50 focus:ring-offset-2"
                        >
                            <XCircle className="w-5 h-5" />
                            Reject Quote
                        </button>
                        <button
                            onClick={() => setAmendMode((v) => !v)}
                            className="flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:ring-offset-2"
                        >
                            <Edit3 className="w-5 h-5" />
                            {amendMode ? 'Cancel Amend' : 'Amend Quote'}
                        </button>
                    </div>
                )}

                {amendMode && canApprove && (
                    <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-gray-200 dark:border-slate-700 p-6 print:hidden">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-bold text-gray-800 dark:text-white flex items-center gap-2">
                                <Edit3 className="w-5 h-5 text-brand-600" />
                                Amend Quote Details
                            </h3>
                            <button
                                onClick={() => setAmendMode(false)}
                                aria-label="Close amend"
                                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500/40 rounded"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <form onSubmit={handleAmend} className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                <AmendField
                                    label="Marine Rate (%)"
                                    name="marine_rate"
                                    value={amendForm.marine_rate}
                                    onChange={(v) => setAmendForm((p) => ({ ...p, marine_rate: v }))}
                                    error={amendErrors.marine_rate}
                                    type="number"
                                    step="0.01"
                                />
                                <AmendField
                                    label="War Rate (%)"
                                    name="war_rate"
                                    value={amendForm.war_rate}
                                    onChange={(v) => setAmendForm((p) => ({ ...p, war_rate: v }))}
                                    error={amendErrors.war_rate}
                                    type="number"
                                    step="0.01"
                                />
                                <AmendField
                                    label="Sum Insured"
                                    name="sum_insured"
                                    value={amendForm.sum_insured}
                                    onChange={(v) => setAmendForm((p) => ({ ...p, sum_insured: v }))}
                                    error={amendErrors.sum_insured}
                                    type="number"
                                />
                                <AmendField
                                    label="Bank Interest / Financial Institution"
                                    name="custom_bank_interest"
                                    value={amendForm.custom_bank_interest}
                                    onChange={(v) => setAmendForm((p) => ({ ...p, custom_bank_interest: v }))}
                                    type="text"
                                />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">
                                        Interests Insured
                                    </label>
                                    <textarea
                                        value={amendForm.interests_insured}
                                        onChange={(e) => setAmendForm((p) => ({ ...p, interests_insured: e.target.value }))}
                                        rows={3}
                                        className="w-full border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-white rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-brand-500/50 focus:border-brand-500 outline-none transition-all resize-none"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">
                                        Comments / Conditions
                                    </label>
                                    <textarea
                                        value={amendForm.comments_conditions}
                                        onChange={(e) => setAmendForm((p) => ({ ...p, comments_conditions: e.target.value }))}
                                        rows={3}
                                        className="w-full border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-white rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-brand-500/50 focus:border-brand-500 outline-none transition-all resize-none"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">
                                    Amendment Notes
                                </label>
                                <textarea
                                    value={amendForm.amendment_notes}
                                    onChange={(e) => setAmendForm((p) => ({ ...p, amendment_notes: e.target.value }))}
                                    rows={2}
                                    placeholder="Describe the changes being made..."
                                    className="w-full border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-white rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-brand-500/50 focus:border-brand-500 outline-none transition-all resize-none"
                                />
                            </div>

                            {quote && (
                                <div className="bg-brand-500/5 dark:bg-brand-500/10 border border-brand-500/20 rounded-xl p-4">
                                    <p className="text-xs font-semibold text-brand-600 dark:text-green-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                                        <TrendingUp className="w-3.5 h-3.5" />
                                        New Premium Preview
                                    </p>
                                    <AmendPreview
                                        form={amendForm}
                                        currency={quote.currency}
                                        mode={quote.mode_of_transport}
                                        oldTotal={toNumber(quote.total_premium, 0)}
                                    />
                                </div>
                            )}

                            <div className="flex gap-3 pt-2">
                                <button
                                    type="button"
                                    onClick={() => setAmendMode(false)}
                                    className="flex-1 py-2.5 border border-gray-200 dark:border-slate-600 rounded-lg text-gray-600 dark:text-slate-400 font-medium hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={savingAmend}
                                    className="flex-1 py-2.5 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-60 transition-colors shadow-lg flex items-center justify-center gap-2"
                                >
                                    {savingAmend ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                                    {savingAmend ? 'Saving...' : 'Save Amendments'}
                                </button>
                            </div>
                        </form>
                    </div>
                )}

                <div
                    ref={printRef}
                    className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-gray-200 dark:border-slate-700 overflow-hidden"
                >
                    <div className="bg-gradient-to-r from-[#1e3b29] via-[#3a663f] to-[#4d8c54] p-6 text-white">
                        <div className="flex items-center justify-between flex-wrap gap-3">
                            <div>
                                <div className="flex items-center gap-2 text-green-200 text-sm">
                                    <FileText className="w-4 h-4" />
                                    <span>Marine Cargo Quotation</span>
                                </div>
                                <h2 className="text-2xl font-bold mt-1 font-mono">{quote.quote_number}</h2>
                            </div>
                            <div className="text-right">
                                <p className="text-green-200 text-xs uppercase tracking-wider">Status</p>
                                <Badge
                                    status={quote.status}
                                    className="bg-white/20 text-white border-0 text-sm px-4 py-1.5 rounded-full"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="p-6 space-y-6">
                        <Section
                            id="parties"
                            icon={Users}
                            title="Parties"
                            expanded={expandedSections.parties}
                            onToggle={() => toggleSection('parties')}
                        >
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <BigTile
                                    icon={UserCheck}
                                    label="Insured"
                                    value={quote.insured_name || 'N/A'}
                                />
                                <BigTile
                                    icon={Building}
                                    label="Agent / Broker"
                                    value={quote.agent_name || quote.created_by_name || 'N/A'}
                                />
                            </div>
                        </Section>

                        <Section
                            id="shipment"
                            icon={Ship}
                            title="Shipment Details"
                            expanded={expandedSections.shipment}
                            onToggle={() => toggleSection('shipment')}
                        >
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                <Detail icon={Ship} label="Vessel" value={quote.vessel_name} />
                                <Detail icon={Anchor} label="IMO Number" value={quote.imo_number} />
                                <Detail icon={Globe} label="Mode" value={quote.mode_of_transport} />
                                <Detail icon={FileText} label="Invoice No" value={quote.invoice_number} />
                                <Detail icon={MapPin} label="From (Origin)" value={quote.from_location} />
                                <Detail icon={MapPin} label="To (Destination)" value={quote.to_location} />
                                <Detail icon={Calendar} label="Coverage Start" value={formatDate(quote.coverage_start)} />
                                <Detail icon={Calendar} label="Coverage End" value={formatDate(quote.coverage_end)} />
                            </div>
                        </Section>

                        {(quote.interests_insured || quote.comments_conditions || quote.other_terms) && (
                            <Section
                                id="interests"
                                icon={FileText}
                                title="Interests & Conditions"
                                expanded={expandedSections.interests}
                                onToggle={() => toggleSection('interests')}
                            >
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {quote.interests_insured && (
                                        <TextTile label="Interests Insured" value={quote.interests_insured} />
                                    )}
                                    {quote.comments_conditions && (
                                        <TextTile label="Comments / Conditions" value={quote.comments_conditions} />
                                    )}
                                    {quote.other_terms && (
                                        <TextTile
                                            label="Other Terms & Conditions"
                                            value={quote.other_terms}
                                            className="md:col-span-2"
                                        />
                                    )}
                                </div>
                            </Section>
                        )}

                        <div className="border-t border-gray-200 dark:border-slate-700 pt-4">
                            <div className="text-center py-6 bg-gradient-to-br from-brand-500/5 to-brand-500/10 dark:from-brand-500/10 dark:to-brand-500/20 rounded-xl border border-brand-500/20 dark:border-brand-500/30">
                                <p className="text-xs text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                                    Total Sum Insured
                                </p>
                                <p className="text-4xl font-extrabold text-brand-600 dark:text-green-400 mt-1 tabular-nums">
                                    {formatMoney(quote.sum_insured, quote.currency)}
                                </p>
                            </div>
                        </div>

                        <Section
                            id="premium"
                            icon={Percent}
                            title={`Premium Breakdown (${quote.currency})`}
                            expanded={expandedSections.premium}
                            onToggle={() => toggleSection('premium')}
                        >
                            <div className="space-y-1 bg-gray-50 dark:bg-slate-700/30 rounded-xl p-4">
                                <PremiumRow label="Marine Premium" value={quote.marine_premium} currency={quote.currency} />
                                <PremiumRow label="War Premium" value={quote.war_premium} currency={quote.currency} />
                                <PremiumRow
                                    label="Basic Premium"
                                    value={toNumber(quote.marine_premium, 0) + toNumber(quote.war_premium, 0)}
                                    currency={quote.currency}
                                    bold
                                />
                                <hr className="border-gray-200 dark:border-slate-600 my-2" />

                                <PremiumRow
                                    label="MCI Fee (15% of Basic Premium)"
                                    value={quote.mci_fee}
                                    currency={quote.currency}
                                    highlight
                                />

                                {quote.custom_bank_interest && (
                                    <div className="flex justify-between items-baseline py-1.5">
                                        <span className="text-gray-600 dark:text-slate-300 flex items-center gap-1.5">
                                            <Landmark className="w-3.5 h-3.5" />
                                            Bank Interest / Financial Institution
                                        </span>
                                        <span className="font-medium text-gray-700 dark:text-slate-200">
                                            {quote.custom_bank_interest}
                                        </span>
                                    </div>
                                )}

                                <hr className="border-gray-200 dark:border-slate-600 my-2" />
                                <PremiumRow label="Stamp Duty" value={quote.stamp_duty} currency={quote.currency} />
                                <PremiumRow label="PHCF (0.25%)" value={quote.phcf} currency={quote.currency} />
                                <PremiumRow label="Training Levy (0.20%)" value={quote.training_levy} currency={quote.currency} />
                                <hr className="border-brand-500/30 dark:border-green-400/30 my-2" />
                                <PremiumRow
                                    label="TOTAL PREMIUM"
                                    value={quote.total_premium}
                                    currency={quote.currency}
                                    highlight
                                />
                            </div>
                        </Section>

                        <Section
                            id="attachments"
                            icon={Paperclip}
                            title={`Named Attachments${singleAttachments.length > 0 ? ` (${singleAttachments.length})` : ''}`}
                            expanded={expandedSections.attachments}
                            onToggle={() => toggleSection('attachments')}
                        >
                            {singleAttachments.length === 0 ? (
                                <p className="text-sm text-gray-400 dark:text-slate-500 italic">
                                    No named attachments uploaded for this quote.
                                </p>
                            ) : (
                                <div className="space-y-2">
                                    {singleAttachments.map((att) => (
                                        <AttachmentRow
                                            key={att.key}
                                            label={att.label}
                                            path={att.path}
                                            canRemove={canRemoveAttachments}
                                            removing={removingAttachment === att.key}
                                            onView={() => handleViewAttachment(att.path)}
                                            onDownload={() =>
                                                handleDownloadAttachment(att.path, getBasename(att.path))
                                            }
                                            onRemove={() => handleRemoveAttachment(att.key)}
                                        />
                                    ))}
                                </div>
                            )}
                            {!canRemoveAttachments && singleAttachments.length > 0 && (
                                <p className="text-xs text-gray-400 dark:text-slate-500 italic mt-2">
                                    {isDraft || isPending
                                        ? 'Only admin and underwriter roles can remove attachments once the quote has been submitted for review.'
                                        : 'Attachments can no longer be removed after the quote has been approved or rejected.'}
                                </p>
                            )}
                        </Section>

                        <Section
                            id="additionalAttachments"
                            icon={Paperclip}
                            title={`Additional Documents${additionalAttachments.length > 0 ? ` (${additionalAttachments.length})` : ''}`}
                            expanded={expandedSections.additionalAttachments}
                            onToggle={() => toggleSection('additionalAttachments')}
                        >
                            {additionalAttachments.length === 0 ? (
                                <p className="text-sm text-gray-400 dark:text-slate-500 italic">
                                    No additional documents uploaded.
                                </p>
                            ) : (
                                <div className="space-y-2">
                                    {additionalAttachments.map((att) => (
                                        <AdditionalAttachmentRow
                                            key={att.id}
                                            attachment={att}
                                            canRemove={canRemoveAttachments}
                                            removing={removingAdditional === att.id}
                                            onView={() => handleViewAdditionalAttachment(att.id)}
                                            onDownload={() =>
                                                handleDownloadAdditionalAttachment(att.id, att.original_name)
                                            }
                                            onRemove={() => handleRemoveAdditionalAttachment(att.id)}
                                        />
                                    ))}
                                </div>
                            )}
                        </Section>

                        <div className="border-t border-gray-200 dark:border-slate-700 pt-6 space-y-3 text-xs text-gray-500 dark:text-slate-400">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <p>
                                        Generated by:{' '}
                                        <strong className="text-gray-700 dark:text-slate-300">
                                            {quote.generated_by || quote.created_by_name || 'N/A'}
                                        </strong>
                                    </p>
                                    <p>
                                        Date:{' '}
                                        {formatDateTime(quote.generated_at || quote.created_at)}
                                        {' '}
                                        <span className="text-gray-400 dark:text-slate-500">(EAT)</span>
                                    </p>
                                </div>
                                <div className="sm:text-right">
                                    {quote.approved_by_name && (
                                        <p className="text-green-600 dark:text-green-400">
                                            <CheckCircle className="w-3 h-3 inline mr-1" />
                                            Approved by: <strong>{quote.approved_by_name}</strong>
                                        </p>
                                    )}
                                    {quote.approved_at && (
                                        <p>
                                            On: {formatDateTime(quote.approved_at)}{' '}
                                            <span className="text-gray-400 dark:text-slate-500">(EAT)</span>
                                        </p>
                                    )}
                                    {quote.amendment_notes && (
                                        <p className="text-amber-600 dark:text-amber-400 mt-1">
                                            Amendments: {quote.amendment_notes}
                                        </p>
                                    )}
                                </div>
                            </div>
                            <div className="mt-4 pt-4 border-t border-gray-100 dark:border-slate-600 space-y-1">
                                <p className="italic">This quotation is valid for 30 days from the date of issue.</p>
                                <p className="italic">Subject to Mayfair Insurance Company Limited terms and conditions.</p>
                            </div>
                        </div>
                    </div>
                </div>

                {isApproved && (
                    <div className="print:hidden">
                        <button
                            onClick={() => setShowConvertConfirm(true)}
                            disabled={convertingToCargo}
                            className="w-full bg-gradient-to-r from-[#1a5632] to-[#3a663f] hover:from-[#2d5231] hover:to-[#4d8c54] text-white font-semibold py-4 rounded-xl shadow-lg shadow-brand-600/20 hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-3 disabled:opacity-60 disabled:cursor-not-allowed text-lg focus:outline-none focus:ring-2 focus:ring-brand-500/50 focus:ring-offset-2"
                        >
                            {convertingToCargo ? (
                                <>
                                    <Loader2 className="w-6 h-6 animate-spin" />
                                    Converting to Cargo Certificate...
                                </>
                            ) : (
                                <>
                                    <FileCheck className="w-6 h-6" />
                                    Convert to Cargo Certificate
                                </>
                            )}
                        </button>
                    </div>
                )}
            </div>

            {showApproveConfirm && (
                <ConfirmModal
                    icon={CheckCircle}
                    iconColor="text-brand-600"
                    title="Approve this quote?"
                    message="The client and agent will be notified that this quote has been approved. This action cannot be undone."
                    confirmLabel="Approve"
                    confirmClass="bg-brand-600 hover:bg-brand-700"
                    loading={approving}
                    onConfirm={handleApprove}
                    onCancel={() => setShowApproveConfirm(false)}
                />
            )}

            {showConvertConfirm && (
                <ConfirmModal
                    icon={FileCheck}
                    iconColor="text-brand-600"
                    title="Convert to Cargo Certificate?"
                    message="You'll be taken to the cargo flow with this quote's details pre-filled. You can review everything before generating the certificate."
                    confirmLabel="Continue to Cargo"
                    confirmClass="bg-brand-600 hover:bg-brand-700"
                    loading={convertingToCargo}
                    onConfirm={handleConvertToCargo}
                    onCancel={() => setShowConvertConfirm(false)}
                />
            )}

            {showRejectModal && (
                <RejectModal
                    reason={rejectionReason}
                    setReason={setRejectionReason}
                    loading={rejecting}
                    onConfirm={handleReject}
                    onCancel={() => {
                        setShowRejectModal(false);
                        setRejectionReason('');
                    }}
                />
            )}

            {toast && <Toast toast={toast} onDismiss={() => setToast(null)} />}
        </div>
    );
}

// =========================================================
// SUB-COMPONENTS
// =========================================================

function Section({ id, icon: Icon, title, expanded, onToggle, children }) {
    return (
        <section className="border-b border-gray-200 dark:border-slate-700 pb-4 last:border-0 last:pb-0">
            <button
                type="button"
                onClick={onToggle}
                aria-expanded={expanded}
                aria-controls={`section-${id}`}
                className="w-full flex items-center justify-between text-left group focus:outline-none focus:ring-2 focus:ring-brand-500/40 rounded-lg py-1"
            >
                <div className="flex items-center gap-2">
                    <Icon className="w-5 h-5 text-brand-600 dark:text-green-400" />
                    <h3 className="font-bold text-gray-800 dark:text-white">{title}</h3>
                </div>
                {expanded ? (
                    <ChevronUp className="w-4 h-4 text-gray-400" />
                ) : (
                    <ChevronDown className="w-4 h-4 text-gray-400" />
                )}
            </button>
            {expanded && (
                <div id={`section-${id}`} className="mt-4">
                    {children}
                </div>
            )}
        </section>
    );
}

function Detail({ icon: Icon, label, value }) {
    return (
        <div className="bg-gray-50 dark:bg-slate-700/30 rounded-lg p-3">
            <div className="flex items-center gap-1.5 text-[10px] text-gray-400 uppercase tracking-wider mb-1">
                {Icon && <Icon className="w-3 h-3" />}
                {label}
            </div>
            <p className="font-medium text-gray-800 dark:text-white text-sm truncate" title={value || 'N/A'}>
                {value || 'N/A'}
            </p>
        </div>
    );
}

function BigTile({ icon: Icon, label, value }) {
    return (
        <div className="bg-gray-50 dark:bg-slate-700/30 rounded-xl p-4">
            <p className="text-xs text-gray-400 uppercase tracking-wider mb-1 flex items-center gap-1">
                <Icon className="w-3 h-3" />
                {label}
            </p>
            <p className="font-bold text-lg text-gray-800 dark:text-white truncate" title={value}>
                {value}
            </p>
        </div>
    );
}

function TextTile({ label, value, className = '' }) {
    return (
        <div className={`bg-gray-50 dark:bg-slate-700/30 rounded-xl p-4 ${className}`}>
            <p className="text-xs text-gray-400 uppercase tracking-wider mb-2">{label}</p>
            <p className="text-sm text-gray-700 dark:text-slate-300 whitespace-pre-line">{value}</p>
        </div>
    );
}

function PremiumRow({ label, value, currency, bold, highlight }) {
    return (
        <div
            className={`flex justify-between items-baseline py-1.5 ${bold ? 'font-semibold' : ''
                } ${highlight
                    ? 'text-lg font-extrabold text-brand-600 dark:text-green-400 border-t-2 border-brand-500/30 dark:border-green-400/30 pt-3 mt-2'
                    : ''
                }`}
        >
            <span className={highlight ? '' : 'text-gray-600 dark:text-slate-300'}>{label}</span>
            <span className={`tabular-nums ${highlight ? '' : 'text-gray-700 dark:text-slate-200'}`}>
                {formatMoney(value, currency, true)}
            </span>
        </div>
    );
}

function AttachmentRow({ label, path, canRemove, removing, onView, onDownload, onRemove }) {
    const displayName = getBasename(path) || label;
    const ext = displayName.split('.').pop()?.toLowerCase() || '';
    const isImage = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg'].includes(ext);
    const isPdf = ext === 'pdf';

    return (
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 bg-gray-50 dark:bg-slate-700/30 border border-gray-200 dark:border-slate-700 rounded-xl px-3 py-3">
            <div className="flex items-center gap-3 flex-1 min-w-0">
                <div
                    className={`p-2 rounded-lg ${isPdf
                        ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
                        : isImage
                            ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                            : 'bg-gray-200 dark:bg-slate-700 text-gray-600 dark:text-slate-300'
                        }`}
                >
                    <FileText className="w-4 h-4" />
                </div>
                <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-800 dark:text-white truncate" title={displayName}>
                        {label}
                    </p>
                    <p className="text-xs text-gray-400 truncate" title={displayName}>
                        {displayName}
                    </p>
                </div>
            </div>

            <div className="flex items-center gap-1 sm:flex-shrink-0 self-end sm:self-auto">
                <button
                    type="button"
                    onClick={onView}
                    title="View"
                    aria-label="View attachment"
                    className="p-2 rounded-lg text-gray-500 hover:text-brand-600 dark:text-slate-400 dark:hover:text-green-400 hover:bg-gray-100 dark:hover:bg-slate-600 transition-colors"
                >
                    <Eye className="w-4 h-4" />
                </button>
                <button
                    type="button"
                    onClick={onDownload}
                    title="Download"
                    aria-label="Download attachment"
                    className="p-2 rounded-lg text-gray-500 hover:text-brand-600 dark:text-slate-400 dark:hover:text-green-400 hover:bg-gray-100 dark:hover:bg-slate-600 transition-colors"
                >
                    <Download className="w-4 h-4" />
                </button>
                {canRemove && onRemove && (
                    <button
                        type="button"
                        onClick={onRemove}
                        disabled={removing}
                        title="Remove"
                        aria-label="Remove attachment"
                        className="p-2 rounded-lg text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50"
                    >
                        {removing ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                            <Trash2 className="w-4 h-4" />
                        )}
                    </button>
                )}
            </div>
        </div>
    );
}

function AdditionalAttachmentRow({ attachment, canRemove, removing, onView, onDownload, onRemove }) {
    const displayName = attachment.original_name || 'Attachment';
    const ext = displayName.split('.').pop()?.toLowerCase() || '';
    const isImage = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg'].includes(ext);
    const isPdf = ext === 'pdf';

    return (
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 bg-gray-50 dark:bg-slate-700/30 border border-gray-200 dark:border-slate-700 rounded-xl px-3 py-3">
            <div className="flex items-center gap-3 flex-1 min-w-0">
                <div
                    className={`p-2 rounded-lg ${isPdf
                        ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
                        : isImage
                            ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                            : 'bg-gray-200 dark:bg-slate-700 text-gray-600 dark:text-slate-300'
                        }`}
                >
                    <FileText className="w-4 h-4" />
                </div>
                <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-800 dark:text-white truncate" title={displayName}>
                        {displayName}
                    </p>
                    <p className="text-xs text-gray-400">
                        {attachment.file_size
                            ? `${(attachment.file_size / 1024).toFixed(1)} KB`
                            : ''}
                    </p>
                </div>
            </div>

            <div className="flex items-center gap-1 sm:flex-shrink-0 self-end sm:self-auto">
                <button
                    type="button"
                    onClick={onView}
                    title="View"
                    aria-label="View attachment"
                    className="p-2 rounded-lg text-gray-500 hover:text-brand-600 dark:text-slate-400 dark:hover:text-green-400 hover:bg-gray-100 dark:hover:bg-slate-600 transition-colors"
                >
                    <Eye className="w-4 h-4" />
                </button>
                <button
                    type="button"
                    onClick={onDownload}
                    title="Download"
                    aria-label="Download attachment"
                    className="p-2 rounded-lg text-gray-500 hover:text-brand-600 dark:text-slate-400 dark:hover:text-green-400 hover:bg-gray-100 dark:hover:bg-slate-600 transition-colors"
                >
                    <Download className="w-4 h-4" />
                </button>
                {canRemove && onRemove && (
                    <button
                        type="button"
                        onClick={onRemove}
                        disabled={removing}
                        title="Remove"
                        aria-label="Remove attachment"
                        className="p-2 rounded-lg text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50"
                    >
                        {removing ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                            <Trash2 className="w-4 h-4" />
                        )}
                    </button>
                )}
            </div>
        </div>
    );
}

function AmendField({ label, name, value, onChange, error, type = 'text', step }) {
    return (
        <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">
                {label}
            </label>
            <input
                type={type}
                step={step}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                aria-invalid={!!error}
                className={`w-full border rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-brand-500/50 focus:border-brand-500 outline-none transition-all dark:bg-slate-700 dark:text-white ${error ? 'border-red-400' : 'border-gray-200 dark:border-slate-600'
                    }`}
            />
            {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
        </div>
    );
}

function AmendPreview({ form, currency, mode, oldTotal }) {
    const si = toNumber(form.sum_insured, 0);
    const mr = toNumber(form.marine_rate, 0);
    const wr = toNumber(form.war_rate, 0);

    const marinePremium = si * (mr / 100);
    const warPremium = si * (wr / 100);
    const basic = marinePremium + warPremium;
    const mciFee = basic * 0.15;
    const stampDuty = mode === 'Air' ? (currency === 'KES' ? 40 : 40 / 150) : si * 0.0005;
    const phcf = basic * 0.0025;
    const training = basic * 0.0020;
    const total = marinePremium + warPremium + mciFee + stampDuty + phcf + training;

    const diff = total - oldTotal;
    const pct = oldTotal > 0 ? (diff / oldTotal) * 100 : 0;

    return (
        <div className="space-y-2">
            <div className="flex justify-between text-sm">
                <span className="text-gray-600 dark:text-slate-400">New Total Premium</span>
                <span className="font-bold text-brand-600 dark:text-green-400 tabular-nums">
                    {formatMoney(total, currency, true)}
                </span>
            </div>
            <div className="flex justify-between text-xs">
                <span className="text-gray-500 dark:text-slate-500">vs Old Total</span>
                <span
                    className={`tabular-nums font-medium ${diff > 0
                        ? 'text-red-600 dark:text-red-400'
                        : diff < 0
                            ? 'text-green-600 dark:text-green-400'
                            : 'text-gray-500'
                        }`}
                >
                    {diff > 0 ? '+' : ''}
                    {formatMoney(diff, currency, true)}
                    {oldTotal > 0 && ` (${pct > 0 ? '+' : ''}${pct.toFixed(1)}%)`}
                </span>
            </div>
        </div>
    );
}

function ConfirmModal({ icon: Icon, iconColor, title, message, confirmLabel, confirmClass, loading, onConfirm, onCancel }) {
    return (
        <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={onCancel}
        >
            <div
                className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl p-6 md:p-8 max-w-md w-full"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="text-center space-y-4">
                    <div className="w-16 h-16 mx-auto rounded-full bg-gray-100 dark:bg-slate-700 flex items-center justify-center">
                        <Icon className={`w-8 h-8 ${iconColor}`} />
                    </div>
                    <div>
                        <h3 className="text-xl font-bold text-gray-800 dark:text-white">{title}</h3>
                        <p className="text-gray-500 dark:text-slate-400 text-sm mt-1">{message}</p>
                    </div>
                    <div className="flex gap-3 pt-2">
                        <button
                            onClick={onCancel}
                            disabled={loading}
                            className="flex-1 py-3 border border-gray-200 dark:border-slate-600 rounded-lg text-gray-600 dark:text-slate-400 font-medium hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={onConfirm}
                            disabled={loading}
                            className={`flex-1 py-3 ${confirmClass} text-white rounded-lg font-semibold disabled:opacity-60 transition-colors shadow-lg flex items-center justify-center gap-2`}
                        >
                            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                            {loading ? 'Working...' : confirmLabel}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function RejectModal({ reason, setReason, loading, onConfirm, onCancel }) {
    return (
        <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={onCancel}
        >
            <div
                className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl p-6 md:p-8 max-w-lg w-full"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="space-y-4">
                    <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0">
                            <XCircle className="w-6 h-6 text-red-600 dark:text-red-400" />
                        </div>
                        <div className="flex-1">
                            <h3 className="text-xl font-bold text-gray-800 dark:text-white">Reject Quote</h3>
                            <p className="text-gray-500 dark:text-slate-400 text-sm mt-1">
                                Please provide a reason. The agent will see this.
                            </p>
                        </div>
                    </div>

                    <div className="flex flex-wrap gap-2">
                        {REJECT_REASONS.map((r) => (
                            <button
                                key={r}
                                type="button"
                                onClick={() => setReason(r)}
                                className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${reason === r
                                    ? 'bg-red-500 text-white border-red-500'
                                    : 'bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600 text-gray-600 dark:text-slate-300 hover:border-red-400'
                                    }`}
                            >
                                {r}
                            </button>
                        ))}
                    </div>

                    <textarea
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="Or write a custom reason..."
                        rows={3}
                        className="w-full border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-white rounded-lg px-4 py-3 focus:ring-2 focus:ring-red-500/50 focus:border-red-500 outline-none transition-all resize-none"
                    />

                    <div className="flex gap-3 pt-2">
                        <button
                            onClick={onCancel}
                            disabled={loading}
                            className="flex-1 py-3 border border-gray-200 dark:border-slate-600 rounded-lg text-gray-600 dark:text-slate-400 font-medium hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={onConfirm}
                            disabled={!reason.trim() || loading}
                            className="flex-1 py-3 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-lg flex items-center justify-center gap-2"
                        >
                            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                            {loading ? 'Rejecting...' : 'Confirm Rejection'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function Toast({ toast, onDismiss }) {
    const isError = toast.type === 'error';
    const Icon = isError ? AlertCircle : CheckCircle;
    return (
        <div className="fixed bottom-6 right-6 z-50">
            <div
                className={`flex items-start gap-3 bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border ${isError
                    ? 'border-red-200 dark:border-red-800'
                    : 'border-green-200 dark:border-green-800'
                    } px-5 py-4 min-w-[280px] max-w-md`}
            >
                <Icon
                    className={`w-5 h-5 flex-shrink-0 mt-0.5 ${isError ? 'text-red-500' : 'text-green-500'
                        }`}
                />
                <p className="text-sm text-gray-800 dark:text-white flex-1">{toast.message}</p>
                <button
                    onClick={onDismiss}
                    aria-label="Dismiss"
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                >
                    <X className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
}

function SkeletonLoader() {
    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-slate-900 dark:to-slate-800">
            <div className="max-w-5xl mx-auto px-4 py-8 space-y-6 animate-pulse">
                <div className="h-10 bg-gray-200 dark:bg-slate-700 rounded-lg w-1/4" />
                <div className="h-32 bg-gray-200 dark:bg-slate-700 rounded-2xl" />
                <div className="h-96 bg-gray-100 dark:bg-slate-700 rounded-2xl" />
            </div>
        </div>
    );
}